<template>
  <div id="user-statistics-statistics">
    <Breadcrumbs
      :breadcrumbs="breadcrumbs">

      <div slot="breadcrumbAfter" class="d-flex align-items-center">
        <va-popover
          v-if="userIsSupervisor"
          class="ml-2"
          placement="left"
          :message="$t('statistics2.ops_stats.downloadExcel')">
          <span class="icon i-cloud-download" @click.prevent="showDownloadExcelModal=true"></span>
        </va-popover>
      </div>
    </Breadcrumbs>

    <section class="filters row evollu-form">

      <div class="col-12 col-md-3">
        <range-date-picker
          id="statistics-date-range"
          :label="$t('clinical.waiting_room_statistics.date_range')"
          v-model="filters.dateRange"
          placeholder="---"
          :config="rangeDatePickerConfig"
          :clearable="false"
        >
        </range-date-picker>
      </div>

      <div class="col-12 col-md-3">
        <label class="form-label">
          {{ $t('clinical.waiting_room_statistics.entity_of_origin') }}
        </label>
        <multiselect
          id="entities-filter"
          v-model="filters.entities"
          :options="entityOptions"
          track-by="uid"
          :customLabel="formatedEntityLabel"
          :placeholder="entityOptions.length ? $t('clinical.waiting_room_statistics.several_entities') : $t('clinical.waiting_room_statistics.no_entities')"
          :allow-empty="true"
          :multiple="true"
          :disabled="!entityOptions.length || isRequestingData"
        >

          <template slot="clear">
            <span class="multiselect__clear" v-if="filters.entities.length" @mousedown.prevent.stop="filters.entities = []"></span>
          </template>
        </multiselect>
      </div>

      <div class="col-12 col-md-3" v-if="userIsSupervisor && entityOptions.length">
        <label class="form-label">
          {{ $t('clinical.waiting_room_statistics.collaborators') }}
        </label>
        <multiselect
          id="collaborators-filter"
          v-model="filters.audiologists"
          :options="userOptions"
          track-by="id"
          :customLabel="formatedUserLabel"
          :placeholder="userOptions.length ? $t('clinical.waiting_room_statistics.all_collaborators') : $t('clinical.waiti ng_room_statistics.no_collaborators')"
          :allow-empty="true"
          :multiple="true"
          :disabled="!userOptions.length || isRequestingData"
        >

          <template slot="clear">
            <span class="multiselect__clear" v-if="filters.audiologists.length > 0" @mousedown.prevent.stop="filters.audiologists = []"></span>
          </template>
        </multiselect>
      </div>

      <div class="col-12 col-md-3" v-if="userIsSupervisor">
        <label class="form-label">
          {{ $t('clinical.appointments.type') }}
        </label>
        <multiselect
          id="appt-type-filter"
          v-model="filters.appointmentTypes"
          :options="appointmentTypesOptions"
          track-by="id"
          label="type"
          placeholder="---"
          :allow-empty="true"
          :multiple="true"
          :close-on-select="false"
        >

          <template slot="clear">
            <span class="multiselect__clear" v-if="filters.appointmentTypes.length > 0" @mousedown.prevent.stop="filters.appointmentTypes = []"></span>
          </template>
        </multiselect>
      </div>
    </section>

    <fade-transition :duration="200" mode="out-in">
      <section v-if="isLoading" class="text-center py-5">
        <span class="is-bold">{{$t('loading.message')}}</span>
      </section>

      <section v-else class="dispenser-center-statistics" key="dispenser-center-statistics">

        <div class="py-4">
          <div class="row px-2 mb-5">
            <KPICard
              class="col-12 col-sm-6 col-md-4 col-lg-3 mb-5"
              :title="originalData.completed_requests"
              :subtitle="$t('clinical.waiting_room_statistics.call_requests')"
            />

            <KPICard
              class="col-12 col-sm-6 col-md-4 col-lg-3 mb-5"
              :subtitle="$t('clinical.waiting_room_statistics.average_appointment_duration')"
            >
              <template slot="title">
                <h3 class="card__container__main__title py-1" v-if="originalData.avg_attendance_duration < 3600">
                  {{getRelativeTimeString(originalData.avg_attendance_duration)}}
                </h3>

                <h4 class="card__container__main__title py-2" v-else>
                  {{getRelativeTimeString(originalData.avg_attendance_duration)}}
                </h4>
              </template>
            </KPICard>

            <KPICard
              class="col-12 col-sm-6 col-md-4 col-lg-3 mb-5"
              :subtitle="$t('clinical.waiting_room_statistics.average_call_request_duration')"
            >
              <template slot="title">
                <h3 class="card__container__main__title py-1" v-if="originalData.avg_request_duration < 3600">
                  {{getRelativeTimeString(originalData.avg_request_duration)}}
                </h3>

                <h4 class="card__container__main__title py-2" v-else>
                  {{getRelativeTimeString(originalData.avg_request_duration)}}
                </h4>
              </template>
            </KPICard>

            <KPICard
              class="col-12 col-sm-6 col-md-4 col-lg-3 mb-5"
              :subtitle="$t('clinical.waiting_room_statistics.average_conclusion_duration')"
            >
              <template slot="title">
                <h3 class="card__container__main__title py-1" v-if="originalData.avg_conclusion_duration < 3600">
                  {{getRelativeTimeString(originalData.avg_conclusion_duration)}}
                </h3>

                <h4 class="card__container__main__title py-2" v-else>
                  {{getRelativeTimeString(originalData.avg_conclusion_duration)}}
                </h4>
              </template>
            </KPICard>

          </div>

          <!-- Avaliações desativadas temporariamente (manter página de avaliações de consultas)
          <div class="row">
            <KPICard
              class="col-12 col-sm-6 col-md-4 col-lg-3 mb-5"
              :title="originalData.avg_dispenser_rating.toFixed(2)"
              :subtitle="$t('clinical.waiting_room_statistics.users.average_dispenser_rating')"
            />

            <KPICard
              class="col-12 col-sm-6 col-md-4 col-lg-3 offset-lg-2 mb-5"
              :title="originalData.avg_audiologist_rating.toFixed(2)"
              :subtitle="$t('clinical.waiting_room_statistics.users.average_audiologist_rating')"
            />
          </div>
          -->
        </div>
      </section>

    </fade-transition>

    <DownloadExcelModal
      exportType="audiologists"
      v-if="showDownloadExcelModal"
      @modalClosed="exportExcelClosed"
    />

  </div>

</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { get as _get, uniqBy } from 'lodash'
import Multiselect from 'vue-multiselect'
import { HollowDotsSpinner } from 'epic-spinners'
import KPICard from '@/components/common/KPICard'
import {ActivityService, EntityService} from '@/api/AuthService'
import Breadcrumbs from '@/components/common/Breadcrumbs'
import RelativeTimeMixin from '@/mixins/RelativeTimeMixin'
import { StatisticsService } from '@/api/VirtualWaitingRoomService'
import RangeDatePicker from '@/components/common/RangeDatePicker'
import LocalStorageHelper from '@/local-storage/LocalStorageHelper'
import ClustersAndEntitiesMixin from '@/mixins/ClustersAndEntitiesMixin'
import { WAITING_ROOM_AUDIOLOGIST_STATISTICS } from '@/local-storage/LocalStorageKeys'
import FadeTransition from '@/components/common/transitions/FadeTransition'
import MultiselectLabelFormatter from '@/mixins/MultiselectLabelFormatter'
import DownloadExcelModal from '@/components/virtual_waiting_room/statistics/DownloadExcelModal.vue';

const AudiologyActivityId = 1
const ReadAccessType = 1
const WriteAccessType = 2
const AudiologistStatisticsInfoType = 700


export default {
  props: {
    component: {
      type: Object,
      required: true
    },
    serviceSlug: {
      type: String,
      required: true
    }
  },
  components: {
    DownloadExcelModal,
    KPICard,
    Breadcrumbs,
    Multiselect,
    FadeTransition,
    RangeDatePicker,
    HollowDotsSpinner
  },
  mixins: [
    RelativeTimeMixin,
    ClustersAndEntitiesMixin,
    MultiselectLabelFormatter,
  ],
  computed: {
    waitingRoomModuleKey: function () {
      return `virtualWaitingRoom`
    },
    ...mapGetters([
      'clusters'
    ]),
    ...mapGetters('auth', [
      'authUserEntityActivities'
    ]),
    ...mapState('auth', [
      'hasLoadedAuthUserEntityActivities'
    ]),
    ...mapState({
      appointmentTypesOptions: function(state) {
        return state[this.waitingRoomModuleKey].appointmentTypes
      }
    }),
    breadcrumbs: function () {
      return [
        { title: this.$t(`menu.${this.serviceSlug}`), disabled: true },
        { title: this.$t('menu.clinical/statistics/waitingroom/audiologists'), active: true },
      ]
    },
    writeAudiologistStatisticsPermissions: function () {
      return this.component.service.user_permissions.filter(p => p.access_type >= WriteAccessType && p.info_type === AudiologistStatisticsInfoType)
    },
    writeStatisticsEntities: function () {
      if (this.component.service !== null) {
        if (this.writeAudiologistStatisticsPermissions.length > 0) {
          if (this.writeAudiologistStatisticsPermissions.some(p => p.entity_id === null)) {
            return [...this.component.service.entities]
          } else {
            return this.component.service.entities.filter(entity => this.writeAudiologistStatisticsPermissions.some(p => p.entity_id === entity.id))
          }
        } else {
          return []
        }
      }
    },
    readAudiologistStatisticsPermissions: function () {
      return this.component.service.user_permissions.filter(p => p.access_type >= ReadAccessType && p.info_type === AudiologistStatisticsInfoType)
    },
    readStatisticsEntities: function () {
      if (this.component.service !== null) {
        if (this.readAudiologistStatisticsPermissions.length > 0) {
          if (this.readAudiologistStatisticsPermissions.some(p => p.entity_id === null)) {
            return [...this.component.service.entities]
          } else {
            return this.component.service.entities.filter(entity => this.readAudiologistStatisticsPermissions.some(p => p.entity_id === entity.id))
          }
        } else {
          return []
        }
      }
    },
    userIsSupervisor: function () {
      if(this.component.service !== null) {
        return this.writeAudiologistStatisticsPermissions.length > 0
      }

      return false;
    },
    userHasReadAudiologistStatisticsPermissions: function () {
      return this.readAudiologistStatisticsPermissions.length > 0
    },
    rangeDatePickerConfig: function () {
      return this.userHasReadAudiologistStatisticsPermissions ? {} : { maxInterval: { value: 6, unit: 'months' } }
    },

    usersFromEntityActivities: function () {
      let users = {}

      this.entityActivityRecordsForPeriod.forEach(entityActivityRecord => {
        if (entityActivityRecord.user !== null) {
          let entry = users[entityActivityRecord.user.id]

          if (!users.hasOwnProperty(entityActivityRecord.user.id)) {
            entry = {
              id: entityActivityRecord.user.id,
              name: entityActivityRecord.user.name,
              nickname: entityActivityRecord.user.nickname,
              entity: this.component.service.entities.find(entity => entity.id === entityActivityRecord.user.entity_id) || null,
              activities: []
            }
            users[entityActivityRecord.user.id] = entry
          }

          entry.activities.push({
            entity_id: entityActivityRecord.entity.id,
            activity_id: entityActivityRecord.activity.id
          })
        }
      })
      users = Object.values(users)

      users.sort((a, b) => {
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      })
      return Object.values(users)
    },
    readableEntities: function () {
      let entities = [...this.readStatisticsEntities]

      if (this.authUserEntityActivities.length) {
        this.authUserEntityActivities.forEach(entityActivityRecord => {
          if (!entities.some(entity => entity.id === entityActivityRecord.entity.id)) {
            entities.push({
              id: entityActivityRecord.entity.id,
              name: entityActivityRecord.entity.name
            })
          }
        })
      }
      return entities
    },
    writableEntities: function () {
      let entities = [...this.writeStatisticsEntities]

      if (this.authUserEntityActivities.length) {
        this.authUserEntityActivities.forEach(entityActivityRecord => {
          if (!entities.some(entity => entity.id === entityActivityRecord.entity.id)) {
            entities.push({
              id: entityActivityRecord.entity.id,
              name: entityActivityRecord.entity.name
            })
          }
        })
      }
      return entities
    },
    userOptions: function () {
      return this.usersFromEntityActivities
    }
  },
  data: function () {
    return {
      originalData: [],
      filters: {
        entities: [],
        audiologists: [],
        dateRange: {
          start: this.moment().startOf('month').format('YYYY-MM-DD'),
          end: this.moment().endOf('month').format('YYYY-MM-DD')
        },
        appointmentTypes:[]
      },
      entityOptions: [],
      selectedEntityIds: [],
      selectedEntityUids: [],
      entityActivityRecordsForPeriod: [],
      isLoading: true,
      isRequestingData: false,
      originalLocalStorageState: LocalStorageHelper.get(WAITING_ROOM_AUDIOLOGIST_STATISTICS),
      showDownloadExcelModal: false,
    }
  },
  created: function () {
    this.loadAuthUserEntityActivityRecords()
    this.setEntityOptions()
  },
  methods: {
    ...mapActions('auth', {
      loadAuthUserEntityActivityRecords: 'LOAD_AUTH_USER_ENTITY_ACTIVITIES'
    }),
    exportExcelClosed: function () {
      this.showDownloadExcelModal = false
    },
    setEntityOptions: function () {
      if (!this.userIsSupervisor && this.readableEntities.length === 1) {
        EntityService.getEntitiesByAudProvider(this.readableEntities[0].id)
          .then(resp => {
            let entities = resp
            entities = entities.filter(entity => entity.aud_provider !== entity.id)
            this.entityOptions = this.createClustersAndEntitiesList(this.clusters, entities)
          })
          .catch(err => {
            console.log(err)
            this.showToast("Error getting entities", this.$store.state.errorToastOptions)
          })

      } else {
        this.entityOptions = this.createClustersAndEntitiesList(this.clusters, this.userIsSupervisor ? this.writableEntities : this.readableEntities)
      }
    },
    loadEntityActivityUsersForPeriod: function () {
      return new Promise((resolve, reject) => {
        let params = {
          accessType: ReadAccessType,
          infoType: AudiologistStatisticsInfoType,
          serviceId: this.component.service.id,
          start: this.moment(this.filters.dateRange.start).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
          end: this.moment(this.filters.dateRange.end).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
          activities: `${AudiologyActivityId}`
        }
        ActivityService.getActivityUsersByEntity(params)
          .then(entityActivityRecords => {
            this.entityActivityRecordsForPeriod = entityActivityRecords
            resolve()
          }).catch(errors => {
          console.log('errors', errors)
          reject(errors)
        })
      })
    },
    loadLocalStorage: function () {
      return new Promise((resolve, reject) => {
        if (this.originalLocalStorageState !== null) {
          if (this.originalLocalStorageState.hasOwnProperty('start') && this.originalLocalStorageState.start !== null) {
            this.filters.dateRange.start = this.originalLocalStorageState.start
          }

          if (this.originalLocalStorageState.hasOwnProperty('end') && this.originalLocalStorageState.end !== null) {
            this.filters.dateRange.end = this.originalLocalStorageState.end
          }

          if(this.originalLocalStorageState.appointmentTypes && this.originalLocalStorageState.appointmentTypes !== '') {
            const filteredIds = this.originalLocalStorageState.appointmentTypes.split(',').map(id => parseInt(id))
            this.filters.appointmentTypes = this.appointmentTypesOptions.filter(elem => elem.bdIds.some(id => filteredIds.includes(id)))
          }

          this.loadEntityActivityUsersForPeriod()
            .finally(() => {
              if (this.originalLocalStorageState.hasOwnProperty('audiologists') && this.originalLocalStorageState.audiologists !== null && this.originalLocalStorageState.audiologists !== '') {
                if (this.userOptions.length) {
                  let userIds = this.originalLocalStorageState.audiologists.split(',').map(id => parseInt(id))
                  this.filters.audiologists = this.userOptions.filter(u => userIds.includes(u.id))
                }
              }

              this.$nextTick(() => {
                this.hasLoadedLocalStorage = true
                resolve()
              })
            })
        } else {
          this.loadEntityActivityUsersForPeriod()
            .finally(() => {
              this.$nextTick(() => {
                this.hasLoadedLocalStorage = true
                resolve()
              })
            })
        }
      })
    },
    loadEntityOfOriginFromLocalStorage: function () {
      return new Promise((resolve, reject) => {
        if (this.originalLocalStorageState !== null && Array.isArray(this.originalLocalStorageState.selectedEntityUids) && this.originalLocalStorageState.selectedEntityUids.length) {
          this.filters.entities = this.reloadSelectedClustersAndEntities(this.entityOptions, this.originalLocalStorageState.selectedEntityUids)
        }
        resolve()
      })
    },
    getActiveFilters: function () {
      return {
        start: _get(this.filters, 'dateRange.start', null) || undefined,
        end: _get(this.filters, 'dateRange.end', null) || undefined,
        ...this.filters.audiologists.length && { audiologists: this.filters.audiologists.map(user => user.id).join(',') },
        ...this.filters.entities.length && { entities: this.filters.entities.map(entity => entity.id).join(',') },
        isRequesteeSupervisor: this.userIsSupervisor,
        appointmentTypes: this.filters.appointmentTypes.flatMap(type => type.bdIds).join(',')
      }
    },
    getLocalStorageFilters: function () {
      return {
        ...this.getActiveFilters(),
        audiologists: this.filters.audiologists.length > 0 ? this.filters.audiologists.map(u => u.id).join(',') : '',
        selectedEntityUids: this.selectedEntityUids,
        appointmentTypes: this.filters.appointmentTypes.flatMap(type => type.bdIds).join(',')
      }
    },
    getStatistics: function () {
      return new Promise((resolve, reject) => {
        StatisticsService.getAudiologistStatistics(this.component.service.url, this.getActiveFilters())
          .then(data => {
            this.originalData = data
            resolve()
          })
          .catch(errors => {
            console.log(errors)
            this.showToast(this.$t('clinical.waiting_room_statistics.something_went_wrong'), this.$store.state.errorToastOptions)
            reject(errors)
          })
      })
    }
  },
  watch: {
    userOptions: function (newValue) {
      if (this.filters.audiologists.some(user => !newValue.some(userOption => userOption.id === user.id))) {
        this.filters.audiologists = this.filters.audiologists.filter(user => newValue.some(userOption => userOption.id === user.id))
      }
    },
    'filters.dateRange': {
      handler: function () {
        if (this.hasLoadedLocalStorage) {
          this.isRequestingData = true
          this.loadEntityActivityUsersForPeriod()
          this.getStatistics()
            .then(() => {
              this.isRequestingData = false
            })
        }
      },
      deep: true
    },
    'filters.audiologists': function (value) {
      if (this.hasLoadedLocalStorage) {
        this.isRequestingData = true
        this.getStatistics()
          .then(() => {
            this.isRequestingData = false
          })
      }
    },
    entityOptions: function (entityOptions) {
      this.filters.entities = this.filters.entities.filter(entity => entityOptions.some(option => option.id === entity.id))
    },
    'filters.entities': function (options) {
      const selectedOptions = this.getSelectedOptionsIdsAndUids(options)
      this.selectedEntityIds = selectedOptions.ids
      this.selectedEntityUids = selectedOptions.uids
      this.isRequestingData = true
      this.getStatistics()
        .then(() => {
          this.isRequestingData = false
        })
    },
    'filters.appointmentTypes': function (options) {
      this.getStatistics()
        .then(() => {
          this.isRequestingData = false
        })
    },
    'filters': {
      handler: function () {
        LocalStorageHelper.set(WAITING_ROOM_AUDIOLOGIST_STATISTICS, this.getLocalStorageFilters())
      },
      deep: true
    },
    'hasLoadedAuthUserEntityActivities': {
      handler: function (hasLoaded) {
        if (hasLoaded) {
          this.loadLocalStorage()
            .then(() => this.getStatistics())
            .then(() => {
              this.loadEntityOfOriginFromLocalStorage()
              this.isLoading = false
            })
        }
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss">
#user-statistics {
  .kpi-card {
    min-height: 12rem;
  }
}
</style>
